import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { TrackFormatInfo, copyTrackFormatInfo } from '@/features/trackFormats/trackFormatsSlice';

import Button from '@/components/Common/Button';
import FlexRow from '@/components/Common/FlexRow';
import Input from '@/components/Common/Input';

import { CatHeader } from '@/pages/Projects/Project';

export interface TrackFormatProps {
	index:				number;
	trackFormat:		TrackFormatInfo | null;
	setTrackFormat:		(trackFormat: TrackFormatInfo) => any;
	onClose:			() => any;
}

const TrackFormat = (props: TrackFormatProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const isNew = props.index == -2;

  const [trackFormat, setTrackFormat] = React.useState(null as TrackFormatInfo | null);
  const [changes, setChanges] = React.useState(false);
  const [triedSave, setTriedSave] = React.useState(false);

  React.useEffect(() => {
    setTrackFormat(props.trackFormat);
  }, [props.trackFormat]);

  const keyInvalid = React.useMemo(() => {
    return !trackFormat || trackFormat.key.trim() == '';
  }, [trackFormat]);

  const descriptionInvalid = React.useMemo(() => {
    return !trackFormat || trackFormat.description.trim() == '';
  }, [trackFormat]);

  const updateKey = React.useCallback((val: string) => {
    if (!trackFormat)
      return;

    let ntf = copyTrackFormatInfo(trackFormat);
    ntf.key = val;

    setTrackFormat(ntf);
    setChanges(true);
  }, [trackFormat, setTrackFormat]);

  const updateDescription = React.useCallback((val: string) => {
    if (!trackFormat)
      return;

    let ntf = copyTrackFormatInfo(trackFormat);
    ntf.description = val;

    setTrackFormat(ntf);
    setChanges(true);
  }, [trackFormat, setTrackFormat]);

  const handleSave = React.useCallback(() => {
    if (!trackFormat || keyInvalid || descriptionInvalid) {
      setTriedSave(true);
      return;
    }

    setTriedSave(false);
    props.setTrackFormat(trackFormat);
    setChanges(false);
    props.onClose();
  }, [trackFormat, props.setTrackFormat, keyInvalid, descriptionInvalid]);

  if (!trackFormat)
    return;

  return (
  <React.Fragment>
    <FlexRow direction="column"
             align="start"
             className="px-[32px]">
      <FlexRow direction="column"
		       align="start"
		       className="py-0 gap-[16px] max-w-[620px]">
	    <CatHeader caption={isNew ? 'track_formats:add_track_format:caption' : 'track_formats:track_format:edit_track_format'} />

		<Input label={t('track_formats:key') + '*'}
		       isInvalid={triedSave && keyInvalid}
		       errorMessage={t('track_formats:track_format:key:invalid')}
		       value={trackFormat ? trackFormat.key : ''}
		       onValueChange={updateKey} />
		<Input label={t('track_formats:description') + '*'}
		       isInvalid={triedSave && descriptionInvalid}
		       errorMessage={t('track_formats:track_format:description:invalid')}
		       value={trackFormat ? trackFormat.description : ''}
		       onValueChange={updateDescription} />
	  </FlexRow>
    </FlexRow>
    <FlexRow className="absolute bottom-0 p-[32px] bg-white z-10 justify-end gap-[16px]">
      <Button className="uppercase"
              caption={t('track_formats:cancel')}
              onClick={() => props.onClose()}
              layout="white" />
      <Button caption={t('track_formats:save')}
              icon="save"
              isDisabled={!changes || (triedSave && (keyInvalid || descriptionInvalid))}
              onClick={() => handleSave()} />
      {!isNew && props.trackFormat &&
        <Button caption={t('channels:channels')}
                icon="search"
                onClick={() => navigate('/settings/trackformats/' + encodeURIComponent(props.trackFormat!.key) + '/searchchannel')}
                layout="white" />
      }
      {!isNew && props.trackFormat &&
        <Button caption={t('projects:projects')}
                icon="search"
                onClick={() => navigate('/settings/trackformats/' + encodeURIComponent(props.trackFormat!.key) + '/searchproject')}
                layout="white" />
      }
    </FlexRow>
  </React.Fragment>
  );
};

export default TrackFormat;
